import { Injectable, computed, signal } from '@angular/core';
import { ITokens } from '@libs/interfaces/auth.interfaces';
import { Observable, of, timer } from 'rxjs';
import { catchError, delay, filter, map, switchMap, tap } from 'rxjs/operators';
import { MS_ONE_MIN } from '@libs/helper/constants.helper';
import { MapResponseData } from '@libs/rxjs/operators.rxjs';
import { AUTH_STATE } from '@libs/interfaces';
import { CanActivate, Router } from '@angular/router';
import { IToast, MODAL_TYPE } from '@libs/store/toast/model';
import { ApiService } from '../api.service';
import { LocalstorageService } from './localstorage.service';
import { IModal } from '@libs/store/modal/model';
import { StoreToastModalsService } from '../store/store-toast-modals.service';
import { IsValidToken } from '@libs/helper/tokens.helper';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements CanActivate {
  private state = signal(this.hasValidTokenStored ? AUTH_STATE.HAS_REFRESH_TOKEN : AUTH_STATE.NO_TOKEN);
  logged = computed<boolean>(() => this.state() === AUTH_STATE.HAS_VALID_TOKEN);
  token = signal<string>(null);
  refresh_token = signal<string>(this.hasValidTokenStored ? this.ls.refreshToken : null);
  noRefreshToken = computed(() => this.state() === AUTH_STATE.NO_TOKEN);

  constructor(
    private apiService: ApiService,
    private router: Router,
    private ls: LocalstorageService,
    private storeToastModalService: StoreToastModalsService,
  ) {
    timer(MS_ONE_MIN * 5, MS_ONE_MIN * 5)
      .pipe(
        filter(() => this.state() !== AUTH_STATE.NO_TOKEN),
        switchMap(() => this.refresh()),
      )
      .subscribe();
  }

  /**
   * Determines if the user can activate a route based on their authentication state.
   * @returns {Observable<boolean>} An observable that emits true if the user is logged in, otherwise false.
   */
  canActivate(): Observable<boolean> {
    if (this.logged()) {
      return of(true);
    } else {
      return this.warningNotLogedMessage();
    }
  }

  /**
   * Displays a warning message when the user is not logged in.
   * @returns {Observable<boolean>} An observable that emits true after displaying the warning message.
   */
  private warningNotLogedMessage(): Observable<boolean> {
    if (this.state() === AUTH_STATE.HAS_REFRESH_TOKEN) return of(true);
    const toast: IToast = {
      id: Date.now(),
      info: 'LANDING.NTBL', // Information message for the toast
      type: MODAL_TYPE.WARNING, // Type of the toast message
      exp: 2000, // Expiration time for the toast message
    };
    this.storeToastModalService.addToast(toast); // Dispatch an action to add the toast message
    return of(true).pipe(
      delay(2000),
      tap(() => this.goSign()),
    );
  }

  /**
   * Initialize the authentication process if a refresh token is available.
   * @returns {Observable<boolean>} An observable that emits true if the refresh token is valid, otherwise false.
   */
  init(): Observable<boolean> {
    if (this.state() === AUTH_STATE.HAS_VALID_TOKEN) return of(true);
    if (this.state() === AUTH_STATE.HAS_REFRESH_TOKEN) {
      return this.refresh();
    } else {
      return of(false);
    }
  }

  /**
   * Refresh the authentication tokens by making an API call.
   * @returns {Observable<boolean>} An observable containing the new tokens.
   */
  refresh(): Observable<boolean> {
    return this.apiService.get('auth/refresh/app').pipe(
      catchError(() => of(null)),
      MapResponseData,
      map((tokens: ITokens) => {
        if (tokens) return this.storeTokens(tokens);
        else {
          this.removeTokens();
          this.goSign();
          return false;
        }
      }),
    );
  }

  /**
   * Navigate to the sign-in page and set the authentication state to unauthenticated.
   */
  private goSign(): void {
    this.router.navigate(['sign']);
  }

  /**
   * Log out the user by removing tokens and setting the authentication state to unauthenticated.
   */
  logout(): void {
    const modal: IModal = {
      subject: 'LANDING.YHBLOA',
      info: 'LANDING.VQSA',
      onAccept: this.onSucessLogoout(),
      onCancel: of(true),
    };
    this.storeToastModalService.addModal(modal);
  }

  /**
   * Handles the blocked user logout process.
   */
  blockedUser(): void {
    this.removeTokens();
    this.goSign();
  }
  /**
   * Handles the successful logout process.
   * @returns {Observable<boolean>} An observable that emits true after logging out the user.
   */
  private onSucessLogoout(): Observable<boolean> {
    return of(true).pipe(
      tap(() => {
        const toast = {
          id: Date.now(),
          info: 'LANDING.YHBLOA',
          type: MODAL_TYPE.WARNING,
          exp: 2000,
        };
        this.removeTokens();
        this.storeToastModalService.addToast(toast);
        this.router.navigate(['landing']).then(() => {
          window.location.reload();
        });
      }),
    );
  }

  /**
   * Remove the authentication tokens from the session.
   */
  private removeTokens(): void {
    this.ls.removeTokens();
    this.state.set(AUTH_STATE.NO_TOKEN);
  }

  /**
   * Store the authentication tokens in the session.
   * @param {ITokens} tokens - The authentication tokens.
   * @returns {boolean} True if the tokens are valid and stored successfully, otherwise false.
   */
  storeTokens(tokens: ITokens): boolean {
    const { access_token, refresh_token } = tokens;
    if (IsValidToken(access_token) && IsValidToken(refresh_token)) {
      this.token.set(access_token);
      this.refresh_token.set(refresh_token);
      this.ls.storeRefreshToken(refresh_token);
      this.state.set(AUTH_STATE.HAS_VALID_TOKEN);
      return true;
    } else {
      this.removeTokens();
      return false;
    }
  }

  /**
   * Check if a valid refresh token is stored in the session.
   * @returns {boolean} True if a valid refresh token is stored, otherwise false.
   */
  get hasValidTokenStored(): boolean {
    return IsValidToken(this.ls.refreshToken);
  }
  storeRefreshToken(token: string): void {
    this.ls.storeRefreshToken(token);
    this.state.set(AUTH_STATE.HAS_REFRESH_TOKEN);
  }
}
